<template>
  <with-loader :id="id"
               :loading="isLoading"
               class="rounded-lg outline-soft border-soft"
               use-as-overlay
  >
    <perfect-scrollbar ref="horizontalScrollbar" class="whitespace-nowrap sortable-table">
      <t-table :data="tableData"
               :headers="columns"
               :variant="variant"
      >
        <template #thead="{ data, thClass, theadClass, trClass }">
          <thead :class="theadClass">
          <draggable v-model="columns"
                     :class="trClass"
                     class="w-full"
                     filter=".ignore-elements"
                     tag="tr"
                     @end="isDragged = false"
                     @start="isDragged = true"
          >
            <th
              v-for="({ generalClasses, text, columnId, hideSorters, key, titleClasses, hideDrag }) in data"
              :key="key"
              :class="[
                thClass,
                generalClasses,
                { 'cursor-grab': !isDragged && !hideSorters && !hideDrag, 'cursor-grabbing': isDragged }
              ]"
              @mouseenter="handleHeaderCellCursor(true, columnId)"
              @mouseleave="handleHeaderCellCursor(false, columnId)"
            >
              <div class="flex w-full justify-between">
                <!-- 1.hover icon -->
                <vs-icon v-if="displayHoverIcons[columnId] && !hideDrag"
                         :class="isDragged && !hideDrag ? 'cursor-grabbing' : 'cursor-grab'"
                         class="text-white absolute drag-icon"
                         icon="drag_indicator"
                         size="1rem"
                />

                <div class="flex w-full">
                  <!-- 2.outcome icon -->
                  <chart-legend-icon v-if="key" :has-tooltip="true" :type="key" class="relative resolution-icon">
                    <template #tooltip>
                      <h4 class="font-semibold">{{ outcomeLabels[key].label }}</h4>
                      <span v-html="outcomeLabels[key].tooltip"/>
                    </template>
                  </chart-legend-icon>

                  <!-- 3.title -->
                  <div :class="[{ 'cursor-pointer': !hideSorters }, titleClasses, { 'mx-2': !!key }]"
                       class="text-base ignore-elements"
                       @click="() => { if(!hideSorters) handleSorter({
                         columnId: columnId, order: sortChangeMap[sorts[columnId]]
                       }) }"
                       @dragstart="$event.preventDefault()"
                  >
                    {{ text }}
                  </div>
                </div>

                <!-- 4.sort arrows -->
                <sortable-table-arrows v-if="!hideSorters"
                                       :column-id="columnId"
                                       :display-sorters="displayHoverIcons[columnId]"
                                       :sort="sorts[columnId]"
                                       class="block"
                                       @click:sorter="handleSorter"
                />
              </div>
            </th>
          </draggable>
          </thead>
        </template>

        <template #tbody="{ data, tdClass, trClass, tbodyClass, headers }">
          <tbody ref="tableBody" v-bar :class="tbodyClass">
          <div :class="isLoading ? 'h-96' : 'max-h-96'" class="table-body-wrapper">
            <tr v-for="(row, rowIndex) in data"
                :key="rowIndex"
                :class="trClass"
            >
              <td v-for="(cell, cellIndex) in row"
                  :key="cellIndex"
                  :class="[tdClass, headers[cellIndex] && headers[cellIndex].generalClasses]"
              >
                <slot :cell="getCellText(cell, headers[cellIndex] && headers[cellIndex].columnId, rowIndex)"
                      :cell-index="cellIndex"
                      :headers="headers"
                      :row="row"
                      :row-index="rowIndex"
                      :td-class="tdClass"
                      name="td"
                />
              </td>
            </tr>
          </div>
          </tbody>
        </template>
      </t-table>
    </perfect-scrollbar>
  </with-loader>
</template>

<script>
import WithLoader from '@/components/common/WithLoader'
import SortableTableArrows from '@/components/common/SortableTableArrows'
import draggable from 'vuedraggable'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import clone from 'ramda/src/clone'
import move from 'ramda/src/move'
import ChartLegendIcon from '@/components/analytics/charts/ChartLegendIcon'
import LABELS from '@/utils/chart-labels'

export default {
  name: 'SortableTable',

  components: {
    SortableTableArrows,
    WithLoader,
    draggable,
    PerfectScrollbar,
    ChartLegendIcon
  },

  props: {
    id: {
      type: String,
      default: 'table'
    },
    data: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'sortable'
    },
    useFirstColOrder: {
      type: Boolean,
      default: false
    },
    resortOnLoading: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      sorts: {},
      columns: this.headers,
      displayHoverIcons: {},
      sortChangeMap: {
        asc: 'desc',
        desc: 'asc'
      },
      isDragged: false,
      outcomeLabels: LABELS
    }
  },

  computed: {
    tableData() {
      if (!this.useFirstColOrder) {
        return this.data.map((row) => {
          return row.slice(0, this.headers.length)
        })
      }

      let rowNumber = 0
      return this.data.map(row => {
        return [++rowNumber, ...row]
      })
    }
  },

  watch: {
    isLoading: {
      immediate: false,
      handler() {
        if (!this.isLoading && this.resortOnLoading) {
          this.initSorter()
        }
      }
    },
    columns(columns) {
      if (columns[0].columnId === -1) return

      const columnsCopy = clone(columns)
      const orderColumnIndex = columnsCopy.findIndex(({ columnId }) => columnId === -1)
      this.columns = move(orderColumnIndex, 0, columns)
    }
  },

  mounted() {
    window.addEventListener('resize', this.updateHorizontalScrollbar)
    this.initSorter()
    this.updateHorizontalScrollbar()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateHorizontalScrollbar)
  },

  methods: {
    getCellText(cell, colIndex, rowIndex) {
      if (colIndex === -1) return cell

      return this.tableData[rowIndex][colIndex + 1]
    },

    updateHorizontalScrollbar() {
      this.$refs.horizontalScrollbar.ps.update()
    },

    handleSorter({ columnId, order = 'desc' }) {
      this.sorts = {}
      this.sorts[columnId] = order
      this.$emit('click:sorter', { order, columnId })
    },

    initSorter() {
      this.headers.forEach(({ defaultSort, columnId }) => {
        if (defaultSort) {
          this.handleSorter({ columnId: columnId, order: defaultSort })
        }
      })
    },

    handleHeaderCellCursor(isCursorHovering, columnId) {
      this.$set(this.displayHoverIcons, columnId, isCursorHovering)
    }
  }

}
</script>

<style lang="scss">
.sortable-table {
  tr {
    display: flex !important;
  }

  .vb-dragger-styler {
    @apply w-2 mr-0 ml-1;
  }

  .resolution-icon {
    top: 2.5px;
  }

  .drag-icon {
    top: 17.5px;
    margin-left: -15px;
  }
}
</style>
